import { useTranslation } from 'react-i18next';

import Checkbox from '@/components/atoms/Checkbox';
import Tooltip from '@/components/atoms/Tooltip';

import { useChartTheme } from '../theme/ChartThemeContext';

import type { LegendItem } from './types';

import styles from './DatasetLegend.module.scss';

const TOOLTIP_WIDTH = 250;

export interface DatasetLegendProps {
    legendItems: LegendItem[];
    visibleDatasets: Set<number>;
    onToggleDataset: (datasetIndex: number) => void;
}

export default function DatasetLegend(props: DatasetLegendProps) {
    const { legendItems, visibleDatasets, onToggleDataset } = props;
    const { t } = useTranslation();
    // The swatch shows the colour the chart draws the series in, dark lift included.
    const theme = useChartTheme();

    const handleOnToggleDataset = (datasetIndex: number) => {
        // If the only visible dataset is being toggled, do not allow it to be hidden
        const isOnlyVisibleDataset =
            visibleDatasets.size === 1 && visibleDatasets.has(datasetIndex);
        if (isOnlyVisibleDataset) {
            return;
        }
        onToggleDataset(datasetIndex);
    };

    return (
        <div className={styles.legendContainer}>
            {legendItems.map((item) => {
                const isVisible = visibleDatasets.has(item.datasetIndex);
                const tooltipContent = item.tooltipKey ? t(item.tooltipKey) : undefined;

                const legendItemElement = (
                    <LegendItemCheckbox
                        key={`${item.label}-${item.datasetIndex}`}
                        label={item.label}
                        color={theme.color(item.color)}
                        checked={isVisible}
                        onClick={() => handleOnToggleDataset(item.datasetIndex)}
                    />
                );

                if (tooltipContent) {
                    return (
                        <Tooltip
                            key={`${item.label}-${item.datasetIndex}`}
                            content={tooltipContent}
                            maxWidth={TOOLTIP_WIDTH}
                            targetClassName={styles.legendItemTooltipContainer}>
                            <LegendItemCheckbox
                                label={item.label}
                                color={theme.color(item.color)}
                                checked={isVisible}
                                onClick={() => handleOnToggleDataset(item.datasetIndex)}
                            />
                        </Tooltip>
                    );
                }

                return legendItemElement;
            })}
        </div>
    );
}

interface LegendItemCheckboxProps {
    label: string;
    color: string;
    checked: boolean;
    onClick: () => void;
}

function LegendItemCheckbox(props: LegendItemCheckboxProps) {
    const { label, color, checked, onClick } = props;

    return (
        <div className={styles.legendItem} onClick={onClick}>
            {/* The row toggles the series; the box only needs a name for screen readers. */}
            <Checkbox
                checked={checked}
                onCheckedChange={() => {}}
                color={color}
                aria-label={label}
            />
            <span className={styles.legendLabel}>{label}</span>
        </div>
    );
}
